import "./App.css";
import DropdownSelector from "./FunctionalElements/DropdownSelector/DropdownSelector";
import {
  AiOutlineDownload,
  AiOutlineBook,
  AiOutlinePicture,
  AiOutlineDatabase,
} from "react-icons/ai";
import { useState, useRef, useEffect, useCallback } from "react";
import Toggle from "./FunctionalElements/Toggle";
import { fuelOptions } from "./ChartConfigFiles/Options";
import Chart from "./FunctionalElements/Charts/Chart";
import axios from "axios";

import SingleCountrySelector from "./FunctionalElements/CountrySelector/SingleCountrySelector";
import MultiCountrySelector from "./FunctionalElements/CountrySelector/MultiCountrySelector";
import {
  getChartTitle,
  getChartSubtitle,
} from "./HelperFunctions/utilityFunctions";

import { exportComponentAsJPEG } from "react-component-export-image";
import datasetConfig from "./Datasets/datasetConfig.json";
import CsvDownloader from "react-csv-downloader";

import { useData } from "./HelperFunctions/useData";
import handleDownloadData from "./HelperFunctions/handleDownloadData";
import {
  getChartTypeOptions,
  getDataTreatmentOptions,
  isFocusAvailable,
} from "./HelperFunctions/getOptions";
import { toPng } from "html-to-image";

const version_number = "1.3.0";

function EmberDataExplorer({
  initialCountry,
  initalMetric,
  initialDataset,
  initialChartType,
  initialFuel,
  initialComparison,
}) {
  const initialiseDataset = datasetConfig.filter(
    (obj) => obj.value === initialDataset
  )[0];

  const [toolConfig, setToolConfig] = useState({
    dataset: initialiseDataset,
    comparison: initialComparison,
    dataTreatment: initalMetric,
    fuelSelectorStatus:
      initialComparison === "on" && initialiseDataset.multipleSeries
        ? true
        : false,
    chartType: initialComparison === "on" ? "line" : initialChartType,
  });

  const entity = "India";

  // The country that is selected from the CountrySelector
  const [selectedCountry, setSelectedCountry] = useState(initialCountry);

  // The countries selected from the MultiCountrySelector
  const [selectedCountries, setSelectedCountries] = useState(["India Total"]);

  const [fuel, setFuel] = useState({
    value: initialFuel,
    label: initialFuel,
  });

  const [selectedYear, setSelectedYear] = useState(2021);

  const { data, response, isLoading, error, maxValue } = useData(
    toolConfig,
    entity,
    selectedCountry,
    selectedCountries,
    fuel,
    selectedYear
  );

  const [downloadData, setDownloadData] = useState(null);

  // State that holds the unique countries that are available for a dataset.
  // Fetched and reset whenever the dataset changes.
  const [uniqueCountries, setUniqueCountries] = useState([]);

  const chartTitle = getChartTitle(
    toolConfig.dataset,
    selectedCountry,
    toolConfig.comparison,
    toolConfig.chartType,
    fuel
  );

  // The subtitle for the chart based on the dataset and the data treatment
  const chartSubtitle = getChartSubtitle(
    toolConfig.dataset,
    toolConfig.dataTreatment
  );

  const chartRef = useRef(null);

  // Function to change the dataset
  const handleDatasetChange = (option) => {
    setToolConfig((currConfig) => {
      return {
        ...currConfig,
        dataset: option,
        dataTreatment: !option.relativeValue
          ? "absolute"
          : currConfig.dataTreatment,
        comparison:
          currConfig.comparison === "off" && !option.multipleSeries
            ? "on"
            : currConfig.comparison,
        chartType:
          !option.multipleSeries && currConfig.chartType === "area"
            ? "line"
            : currConfig.chartType,
        fuelSelectorStatus:
          currConfig.comparison === "on" && option.multipleSeries
            ? true
            : false,
      };
    });
  };

  const handleDataTreatmentChange = (option) => {
    setToolConfig((currConfig) => {
      return {
        ...currConfig,
        dataTreatment: option,
      };
    });
  };

  const handleComparisonChange = (option) => {
    setToolConfig((currConfig) => {
      return {
        ...currConfig,
        comparison: option,
        chartType:
          option === "on" && currConfig.chartType === "area"
            ? "line"
            : option === "off" && currConfig.chartType === "map"
            ? "area"
            : currConfig.chartType,
        fuelSelectorStatus:
          option === "on" && currConfig.dataset.multipleSeries ? true : false,
      };
    });
  };

  const handleChartTypeChange = (option) => {
    setToolConfig((currConfig) => {
      return {
        ...currConfig,
        chartType: option,
      };
    });
  };

  // Function to change the country (for single country)
  const handleCountryChange = (option) => {
    setSelectedCountry(option);
    setSelectedCountries((selectedCountries) => {
      const baseArray = selectedCountries.slice(1);
      return [option, ...baseArray];
    });
  };

  const handleYearChange = (option) => {
    setSelectedYear(option);
  };

  useEffect(() => {
    setDownloadData(handleDownloadData(toolConfig, response));
  }, [response]);

  // Get the available countries for a dataset
  useEffect(() => {
    const uniqueCountryURL = `https://ember-data-api-scg3n.ondigitalocean.app/ember.json?sql=select+distinct+state+from+${toolConfig.dataset.apiDatasetTitle}+where+country%3D%27${entity}%27+order+by+rowid+limit+2001`;

    axios.get(uniqueCountryURL).then((res) => {
      setUniqueCountries(res.data.rows.flat(1));
    });
  }, [toolConfig.dataset, entity]);

  const onImageExportClick = useCallback(() => {
    if (chartRef.current === null) {
      return;
    }

    toPng(chartRef.current, { cacheBust: true })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = "ember-data-explorer.png";
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.log(err);
      });
  }, [chartRef]);

  return (
    <div className="explorer-element">
      <div className="explorer-container">
        <div className="explorer__top-panel">
          <div className="dropdown-selector--top">
            <DropdownSelector
              header="Choose a dataset"
              options={datasetConfig}
              value={toolConfig.dataset}
              changeSelection={(option) => handleDatasetChange(option)}
            />
          </div>
          <div className="dropdown-selector--top">
            <DropdownSelector
              header="Choose fuel"
              options={fuelOptions}
              value={fuel}
              changeSelection={(option) => setFuel(option)}
              isDisabled={!toolConfig.fuelSelectorStatus}
            />
          </div>
          <div className="toggle--top">
            <div className="toggle__header--top">Choose metric</div>
            <Toggle
              toggleOptions={getDataTreatmentOptions(toolConfig)}
              activeOption={toolConfig.dataTreatment}
              toggleChange={(option) => handleDataTreatmentChange(option)}
            />
          </div>
          <div className="toggle--top">
            <div className="toggle__header--top">Choose chart type</div>
            <Toggle
              toggleOptions={getChartTypeOptions(toolConfig)}
              activeOption={toolConfig.chartType}
              toggleChange={(option) => handleChartTypeChange(option)}
            />
          </div>
        </div>
        <div className="explorer__graph-panel">
          <Chart
            chartRef={chartRef}
            chartTitle={chartTitle}
            chartSubtitle={chartSubtitle}
            fuel={fuel}
            toolConfig={toolConfig}
            selectedEntity={entity}
            selectedCountry={selectedCountry}
            selectedCountries={selectedCountries}
            data={data}
            isLoading={isLoading}
            selectedYear={selectedYear}
            setSelectedYear={(option) => handleYearChange(option)}
            maxValue={maxValue}
          />
        </div>
        <div className="explorer__side-panel">
          <div className="selection-container">
            {toolConfig.comparison === "off" ? (
              <SingleCountrySelector
                toolConfig={toolConfig}
                setComparisonState={(option) => handleComparisonChange(option)}
                selectedCountry={selectedCountry}
                setSelectedCountry={(option) => handleCountryChange(option)}
                uniqueCountries={uniqueCountries}
              />
            ) : (
              <MultiCountrySelector
                toolConfig={toolConfig}
                focusAvailable={isFocusAvailable(toolConfig)}
                setComparisonState={(option) => handleComparisonChange(option)}
                selectedCountries={selectedCountries}
                selectedCountry={selectedCountry}
                setSelectedCountries={(option) => setSelectedCountries(option)}
                setSelectedCountry={(option) => {
                  setSelectedCountry(option);
                }}
                uniqueCountries={uniqueCountries}
              />
            )}
          </div>
          <div className="explorer__utility-panel">
            <CsvDownloader
              filename="emberChartData"
              extension=".csv"
              separator=";"
              meta="true"
              //columns={csvColumns}
              datas={downloadData}
            >
              <button
                title="Download chart data"
                className="utility-button"
                aria-label="Download chart data"
              >
                <AiOutlineDownload size={30} color={"#212b57"} />
              </button>
            </CsvDownloader>
            <form
              action="https://ember-climate.org/data-catalogue/india-electricity-data/"
              target="_blank"
            >
              <button
                className="utility-button"
                title="Get the complete datasets"
              >
                <AiOutlineDatabase size={30} color={"#212b57"} />
              </button>
            </form>
            <button
              className="utility-button"
              title="Export image"
              onClick={onImageExportClick}
            >
              <AiOutlinePicture size={30} color={"#212b57"} />
            </button>
            <form
              action="https://ember-climate.org//app/uploads/2022/11/Ember-Subnational-Data-Methodology.pdf"
              target="_blank"
            >
              <button title="See methodology" className="utility-button">
                <AiOutlineBook size={30} color={"#212b57"} />
              </button>
            </form>
          </div>
        </div>
      </div>
      <div className="version-number">version: {version_number}</div>
    </div>
  );
}

export default EmberDataExplorer;
